/**
 * Retention workspace hooks (RET-050/051/053/054/055/057/058).
 *
 * Wraps the Phase 3 retention surfaces (shapes verified against
 * services/cases/retention.py + services/cases/routes.py):
 *   - GET  /api/v1/retention/queue                          → { items: RetentionQueueItem[] } (RET-050/054)
 *   - GET  /api/v1/retention/paystub-reminders              → { cases: PaystubReminder[] } (RET-051)
 *   - POST /api/v1/retention/paystub-reminders/{case}:send  → PaystubReminder (Maya send, RET-051)
 *   - GET  /api/v1/retention/sequences                      → { items: FollowupSequence[] } (RET-053)
 *   - GET  /api/v1/retention/cob                            → { items: CobItem[] } (RET-055)
 *   - GET  /api/v1/retention/renewals                       → { items: RenewalItem[] } (RET-057)
 *   - GET  /api/v1/retention/disenrollments                 → { items: DisenrollmentItem[] } (RET-058)
 *
 * NB the pay-stub list envelope key is `cases`, not `items`. Each fetch tolerates
 * a bare array and an `{ items }`/`{ cases }` envelope and degrades gracefully on
 * 404 (friendly empty/disabled state, never a crash).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/** trigger for a retention alert / sequence. */
export type RetentionTrigger = 'coverage_change' | 'missed_paystub' | 'inactivity' | 'cob' | string;
/** Severity tier on the wire (RET): low | med | high. */
export type RetentionSeverity = 'low' | 'med' | 'high' | string;

/** A row in the continuous-coverage monitoring queue (RET-050/054). */
export interface RetentionQueueItem {
  id: string;
  case_id: string;
  patient?: string | null;
  trigger: RetentionTrigger;
  severity: RetentionSeverity;
  recommended_action: string;
  detected_at: string;
}

/** A pay-stub reminder tracking row (RET-051). */
export interface PaystubReminder {
  case_id: string;
  /** ISO/date string for the documentation deadline. */
  deadline: string;
  reminders_sent: number;
  /** pending | received | missed. */
  status: string;
}

/** One step in a follow-up sequence (RET-053). */
export interface SequenceStep {
  at: string;
  channel: string;
  status: string;
}

/** A retention follow-up sequence definition (RET-053). */
export interface FollowupSequence {
  id: string;
  trigger: RetentionTrigger;
  channel_mix: string[];
  steps: SequenceStep[];
  stop_condition: string;
}

/** A coordination-of-benefits detection (RET-055). */
export interface CobItem {
  case_id: string;
  evidence: string;
  severity: RetentionSeverity;
}

/** An open-enrollment renewal row (RET-057). */
export interface RenewalItem {
  case_id: string;
  plan_year: number;
  status: string;
}

/** A disenrollment / at-risk row (RET-058). */
export interface DisenrollmentItem {
  case_id: string;
  reason: string;
  status: string;
}

/** Unwrap a list from a bare array or an `{ items }` / `{ cases }` envelope. */
function unwrap<T>(data: T[] | { items?: T[]; cases?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data;
  return data?.items ?? data?.cases ?? [];
}

interface UseRetentionResult {
  alerts: RetentionQueueItem[];
  reminders: PaystubReminder[];
  sequences: FollowupSequence[];
  cob: CobItem[];
  renewals: RenewalItem[];
  disenrollments: DisenrollmentItem[];
  alertsAvailable: boolean;
  remindersAvailable: boolean;
  sequencesAvailable: boolean;
  cobAvailable: boolean;
  renewalsAvailable: boolean;
  disenrollmentsAvailable: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  /** Send (advance) the next pay-stub reminder via Maya for a case (RET-051). */
  sendReminder: (caseId: string) => Promise<boolean>;
}

export function useRetention(): UseRetentionResult {
  const [alerts, setAlerts] = useState<RetentionQueueItem[]>([]);
  const [reminders, setReminders] = useState<PaystubReminder[]>([]);
  const [sequences, setSequences] = useState<FollowupSequence[]>([]);
  const [cob, setCob] = useState<CobItem[]>([]);
  const [renewals, setRenewals] = useState<RenewalItem[]>([]);
  const [disenrollments, setDisenrollments] = useState<DisenrollmentItem[]>([]);
  const [alertsAvailable, setAlertsAvailable] = useState(true);
  const [remindersAvailable, setRemindersAvailable] = useState(true);
  const [sequencesAvailable, setSequencesAvailable] = useState(true);
  const [cobAvailable, setCobAvailable] = useState(true);
  const [renewalsAvailable, setRenewalsAvailable] = useState(true);
  const [disenrollmentsAvailable, setDisenrollmentsAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);

    /** Fetch a list endpoint, set its rows + availability, swallow 404. */
    const load = <T>(
      path: string,
      setRows: (rows: T[]) => void,
      setAvailable: (a: boolean) => void
    ) =>
      apiClient
        .get<T[] | { items?: T[]; cases?: T[] }>(path)
        .then((data) => {
          setRows(unwrap(data));
          setAvailable(true);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 404) {
            setAvailable(false);
            setRows([]);
            return;
          }
          throw err;
        });

    const ps = [
      load<RetentionQueueItem>('/retention/queue', setAlerts, setAlertsAvailable),
      load<PaystubReminder>('/retention/paystub-reminders', setReminders, setRemindersAvailable),
      load<FollowupSequence>('/retention/sequences', setSequences, setSequencesAvailable),
      load<CobItem>('/retention/cob', setCob, setCobAvailable),
      load<RenewalItem>('/retention/renewals', setRenewals, setRenewalsAvailable),
      load<DisenrollmentItem>(
        '/retention/disenrollments',
        setDisenrollments,
        setDisenrollmentsAvailable
      ),
    ];

    Promise.allSettled(ps)
      .then((results) => {
        const failure = results.find(
          (r) => r.status === 'rejected'
        ) as PromiseRejectedResult | undefined;
        if (failure) {
          const reason = failure.reason;
          setError(reason instanceof ApiError ? reason : new ApiError(0, 'ERROR', String(reason)));
        } else {
          setError(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const sendReminder = useCallback(async (caseId: string): Promise<boolean> => {
    setIsWorking(true);
    setActionError(null);
    try {
      const updated = await apiClient.post<PaystubReminder>(
        `/retention/paystub-reminders/${encodeURIComponent(caseId)}:send`
      );
      setReminders((rs) => rs.map((r) => (r.case_id === caseId ? { ...r, ...updated } : r)));
      return true;
    } catch (err: unknown) {
      setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return false;
    } finally {
      setIsWorking(false);
    }
  }, []);

  return {
    alerts,
    reminders,
    sequences,
    cob,
    renewals,
    disenrollments,
    alertsAvailable,
    remindersAvailable,
    sequencesAvailable,
    cobAvailable,
    renewalsAvailable,
    disenrollmentsAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    sendReminder,
  };
}
